import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Briefcase, ArrowLeft, Building2, Plus, MapPin, DollarSign, AlertCircle, CheckCircle2, EyeOff, PlusCircle, UserCheck, Video, TrendingUp } from 'lucide-react';
import { formatPayDisplay } from '../utils/payDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['customer-service','chat-support','data-entry','admin','sales','writing','healthcare','education','marketing','tech-support','tech','accounting','insurance','design'];
const STATES = ['Nationwide','Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];

export default function BusinessPortal() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [showRegister, setShowRegister] = useState(false);
  const [showPostJob, setShowPostJob] = useState(false);
  const [companyForm, setCompanyForm] = useState({ company_name: '', company_description: '', website: '', industry: '', contact_email: user?.email || '' });
  const [jobForm, setJobForm] = useState({ title: '', company: '', description: '', category: 'customer-service', state: 'Nationwide', salary_range: '', equipment_provided: false, quick_apply: true, requirements: '', benefits: '', job_type: 'full-time' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const isBusiness = user?.role === 'business';
  const hasBusinessPlan = ['starter', 'growth', 'enterprise'].includes(user?.membership);

  useEffect(() => {
    if (isBusiness) {
      axios.get(`${API}/business/my-company`, { withCredentials: true })
        .then(r => { setCompany(r.data.company); setJobs(r.data.jobs || []); })
        .catch(console.error);
    }
  }, [isBusiness]);

  const registerCompany = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      await axios.post(`${API}/business/register`, companyForm, { withCredentials: true });
      setSuccess('Company registered successfully!');
      setShowRegister(false);
      const { data } = await axios.get(`${API}/business/my-company`, { withCredentials: true });
      setCompany(data.company);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to register company');
    }
    setLoading(false);
  };

  const postJob = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      await axios.post(`${API}/business/post-job`, {
        ...jobForm,
        company: company?.company_name || jobForm.company,
        requirements: jobForm.requirements.split('\n').filter(Boolean),
        benefits: jobForm.benefits.split('\n').filter(Boolean),
      }, { withCredentials: true });
      setSuccess('Job posted successfully!');
      setShowPostJob(false);
      setJobForm({ title: '', company: '', description: '', category: 'customer-service', state: 'Nationwide', salary_range: '', equipment_provided: false, quick_apply: true, requirements: '', benefits: '', job_type: 'full-time' });
      const { data } = await axios.get(`${API}/business/my-company`, { withCredentials: true });
      setJobs(data.jobs || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to post job');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-[#D97757] flex items-center justify-center">
            <Building2 size={22} className="text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-3xl font-medium tracking-tight" style={{fontFamily:'Outfit'}}>Business Portal</h1>
            <p className="text-[#1C2A24]/60 text-sm">Register your company and post WFH job listings</p>
          </div>
        </div>

        {/* Not a business account — show helpful preview */}
        {!isBusiness && (
          <div className="space-y-6" data-testid="business-preview">
            <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="text-center mb-6">
                <Building2 size={40} className="text-[#D97757] mx-auto mb-3" strokeWidth={1.5} />
                <h2 className="text-xl font-medium mb-2" style={{fontFamily:'Outfit'}}>Business Portal</h2>
                <p className="text-[#1C2A24]/60 text-sm max-w-md mx-auto">Your account is set up as a Job Seeker. Switch to an Employer account to access job posting, applicant tracking, and hiring tools.</p>
              </div>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {[
                  { title: 'Post WFH Jobs', desc: 'Create and manage remote job listings visible to thousands of job seekers', icon: PlusCircle },
                  { title: 'Review Applicants', desc: 'See candidate profiles, resumes, and match scores for your openings', icon: UserCheck },
                  { title: 'Host Hiring Events', desc: 'Run live interviews with a virtual waiting room and real-time chat', icon: Video },
                ].map((f, i) => (
                  <div key={i} className="bg-[#F4F1E8]/30 rounded-2xl p-5 border border-[#E5E0D8]/50">
                    <f.icon size={20} className="text-[#D97757] mb-3" strokeWidth={1.5} />
                    <h3 className="text-sm font-medium mb-1" style={{fontFamily:'Outfit'}}>{f.title}</h3>
                    <p className="text-xs text-[#1C2A24]/50">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <p className="text-xs text-[#1C2A24]/40 mb-3">To get started, create a new Employer account or contact support to switch your current account.</p>
                <Link to="/auth"><Button className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white px-8 h-11" data-testid="create-business-account-btn">Create Employer Account</Button></Link>
              </div>
            </div>
          </div>
        )}

        {/* Business account but no paid plan — preview with upgrade */}
        {isBusiness && !hasBusinessPlan && (
          <div className="space-y-6" data-testid="plan-preview">
            <div className="bg-white rounded-3xl border border-[#D97757]/20 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-[#D97757]/10 flex items-center justify-center shrink-0">
                  <TrendingUp size={22} className="text-[#D97757]" strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="text-xl font-medium mb-1" style={{fontFamily:'Outfit'}}>Activate Your Business Plan</h2>
                  <p className="text-[#1C2A24]/60 text-sm">You have an Employer account. Subscribe to a business plan to unlock all hiring tools.</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-[#F4F1E8]/30 rounded-2xl p-5 border border-[#E5E0D8]/50">
                  <h3 className="text-sm font-medium mb-1" style={{fontFamily:'Outfit'}}>Starter — $99/mo</h3>
                  <ul className="text-xs text-[#1C2A24]/50 space-y-1 mt-2">
                    <li className="flex items-center gap-1"><CheckCircle2 size={10} className="text-[#047857]" /> 3 job posts</li>
                    <li className="flex items-center gap-1"><CheckCircle2 size={10} className="text-[#047857]" /> Live chat hiring events</li>
                    <li className="flex items-center gap-1"><CheckCircle2 size={10} className="text-[#047857]" /> Basic applicant tracking</li>
                  </ul>
                </div>
                <div className="bg-[#D97757]/5 rounded-2xl p-5 border border-[#D97757]/20">
                  <h3 className="text-sm font-medium mb-1 flex items-center gap-1" style={{fontFamily:'Outfit'}}>Growth — $249/mo <span className="text-[9px] bg-[#D97757] text-white px-1.5 py-0.5 rounded-full">Popular</span></h3>
                  <ul className="text-xs text-[#1C2A24]/50 space-y-1 mt-2">
                    <li className="flex items-center gap-1"><CheckCircle2 size={10} className="text-[#047857]" /> 15 job posts</li>
                    <li className="flex items-center gap-1"><CheckCircle2 size={10} className="text-[#047857]" /> Video/Zoom hiring events</li>
                    <li className="flex items-center gap-1"><CheckCircle2 size={10} className="text-[#047857]" /> Waiting room + advanced tracking</li>
                  </ul>
                </div>
                <div className="bg-[#F4F1E8]/30 rounded-2xl p-5 border border-[#E5E0D8]/50">
                  <h3 className="text-sm font-medium mb-1" style={{fontFamily:'Outfit'}}>Enterprise — $499/mo</h3>
                  <ul className="text-xs text-[#1C2A24]/50 space-y-1 mt-2">
                    <li className="flex items-center gap-1"><CheckCircle2 size={10} className="text-[#047857]" /> Unlimited job posts</li>
                    <li className="flex items-center gap-1"><CheckCircle2 size={10} className="text-[#047857]" /> Featured employer badge</li>
                    <li className="flex items-center gap-1"><CheckCircle2 size={10} className="text-[#047857]" /> Analytics + API access</li>
                  </ul>
                </div>
              </div>

              <div className="text-center">
                <Link to="/membership"><Button className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white px-8 h-11" data-testid="get-business-plan">View Business Plans & Subscribe</Button></Link>
              </div>
            </div>
          </div>
        )}

        {/* Has business plan */}
        {isBusiness && hasBusinessPlan && (
          <>
            {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-2xl text-sm mb-4 flex items-center gap-2" data-testid="business-error"><AlertCircle size={16} />{error}</div>}
            {success && <div className="bg-[#A7F3D0]/30 text-[#047857] px-4 py-3 rounded-2xl text-sm mb-4 flex items-center gap-2" data-testid="business-success"><CheckCircle2 size={16} />{success}</div>}

            {/* Company Info or Register */}
            {company ? (
              <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 mb-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="company-info">
                <h2 className="text-xl font-medium mb-4" style={{fontFamily:'Outfit'}}>{company.company_name}</h2>
                <p className="text-[#1C2A24]/70 mb-4">{company.company_description}</p>
                <div className="flex gap-4 text-sm text-[#1C2A24]/50">
                  <span>Industry: {company.industry}</span>
                  {company.website && <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-[#047857]">Website</a>}
                </div>
              </div>
            ) : (
              <>
                {!showRegister ? (
                  <Button onClick={() => setShowRegister(true)} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white mb-6" data-testid="register-company-btn">
                    <Building2 size={16} className="mr-2" /> Register Your Company
                  </Button>
                ) : (
                  <form onSubmit={registerCompany} className="bg-white rounded-3xl border border-[#E5E0D8] p-8 mb-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-4" data-testid="register-company-form">
                    <h2 className="text-xl font-medium" style={{fontFamily:'Outfit'}}>Register Company</h2>
                    <div>
                      <Label>Company Name *</Label>
                      <Input value={companyForm.company_name} onChange={e => setCompanyForm(f => ({...f, company_name: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" required data-testid="company-name-input" />
                    </div>
                    <div>
                      <Label>Description *</Label>
                      <Textarea value={companyForm.company_description} onChange={e => setCompanyForm(f => ({...f, company_description: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={3} required data-testid="company-desc-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Website</Label>
                        <Input value={companyForm.website} onChange={e => setCompanyForm(f => ({...f, website: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" data-testid="company-website-input" />
                      </div>
                      <div>
                        <Label>Industry *</Label>
                        <Input value={companyForm.industry} onChange={e => setCompanyForm(f => ({...f, industry: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" required data-testid="company-industry-input" />
                      </div>
                    </div>
                    <div>
                      <Label>Contact Email *</Label>
                      <Input value={companyForm.contact_email} onChange={e => setCompanyForm(f => ({...f, contact_email: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" required data-testid="company-email-input" />
                    </div>
                    <div className="flex gap-3">
                      <Button type="submit" disabled={loading} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="submit-company-btn">
                        {loading ? 'Registering...' : 'Register Company'}
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setShowRegister(false)} className="rounded-full">Cancel</Button>
                    </div>
                  </form>
                )}
              </>
            )}

            {/* Post Job */}
            {company && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-medium" style={{fontFamily:'Outfit'}}>Your Job Listings ({jobs.length})</h2>
                  <Button onClick={() => setShowPostJob(!showPostJob)} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="post-job-btn">
                    <Plus size={16} className="mr-2" /> Post a Job
                  </Button>
                </div>

                {showPostJob && (
                  <form onSubmit={postJob} className="bg-white rounded-3xl border border-[#E5E0D8] p-8 mb-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-4" data-testid="post-job-form">
                    <h3 className="text-lg font-medium" style={{fontFamily:'Outfit'}}>New Job Posting</h3>
                    <div>
                      <Label>Job Title *</Label>
                      <Input value={jobForm.title} onChange={e => setJobForm(f => ({...f, title: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" required data-testid="job-title-input" />
                    </div>
                    <div>
                      <Label>Description *</Label>
                      <Textarea value={jobForm.description} onChange={e => setJobForm(f => ({...f, description: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={4} required data-testid="job-desc-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Category *</Label>
                        <Select value={jobForm.category} onValueChange={v => setJobForm(f => ({...f, category: v}))}>
                          <SelectTrigger className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" data-testid="job-category-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace('-', ' ')}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>State *</Label>
                        <Select value={jobForm.state} onValueChange={v => setJobForm(f => ({...f, state: v}))}>
                          <SelectTrigger className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" data-testid="job-state-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Salary Range</Label>
                        <Input value={jobForm.salary_range} onChange={e => setJobForm(f => ({...f, salary_range: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" placeholder="e.g. $35,000 - $45,000/year" data-testid="job-salary-input" />
                      </div>
                      <div>
                        <Label>Job Type</Label>
                        <Select value={jobForm.job_type} onValueChange={v => setJobForm(f => ({...f, job_type: v}))}>
                          <SelectTrigger className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="full-time">Full-time</SelectItem>
                            <SelectItem value="part-time">Part-time</SelectItem>
                            <SelectItem value="contract">Contract</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-8">
                      <div className="flex items-center gap-2">
                        <Switch checked={jobForm.equipment_provided} onCheckedChange={v => setJobForm(f => ({...f, equipment_provided: v}))} data-testid="job-equipment-switch" />
                        <Label>Equipment Provided</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={jobForm.quick_apply} onCheckedChange={v => setJobForm(f => ({...f, quick_apply: v}))} data-testid="job-quickapply-switch" />
                        <Label>Quick Apply</Label>
                      </div>
                    </div>
                    <div>
                      <Label>Requirements (one per line)</Label>
                      <Textarea value={jobForm.requirements} onChange={e => setJobForm(f => ({...f, requirements: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={3} data-testid="job-requirements-input" />
                    </div>
                    <div>
                      <Label>Benefits (one per line)</Label>
                      <Textarea value={jobForm.benefits} onChange={e => setJobForm(f => ({...f, benefits: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={3} data-testid="job-benefits-input" />
                    </div>
                    <div className="flex gap-3">
                      <Button type="submit" disabled={loading} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="submit-job-btn">
                        {loading ? 'Posting...' : 'Post Job'}
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setShowPostJob(false)} className="rounded-full">Cancel</Button>
                    </div>
                  </form>
                )}

                {/* Job List */}
                <div className="space-y-3" data-testid="business-jobs-list">
                  {jobs.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-[#E5E0D8] p-8 text-center">
                      <p className="text-[#1C2A24]/50">No jobs posted yet. Click "Post a Job" to get started!</p>
                    </div>
                  ) : jobs.map(job => (
                    <div key={job.id} className="bg-white rounded-2xl border border-[#E5E0D8] p-5 shadow-[0_4px_15px_rgb(0,0,0,0.03)]">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium" style={{fontFamily:'Outfit'}}>{job.title}</h3>
                          <div className="flex items-center gap-3 text-xs text-[#1C2A24]/50 mt-1">
                            <span className="flex items-center gap-1"><MapPin size={12} />{job.state}</span>
                            <span className="flex items-center gap-1">{(() => { const p = formatPayDisplay(job.salary_range); return p.isDisclosed ? <><DollarSign size={12} />{p.text}</> : <><EyeOff size={12} />{p.text}</>; })()}</span>
                            <span>{job.category}</span>
                          </div>
                        </div>
                        <span className="text-xs text-[#1C2A24]/40">{job.applicants || 0} applicants</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
